import { AppState, GraphFilters, ToolState, WalkthroughState, SignUpState } from '../../models';
import calculateGraph from '../graph';
import { Location } from 'history';
import { RouterState } from 'connected-react-router';
import { ApplicationFormState } from '../../models/applicationForm';

const TOOL_STATE_KEY: string = 'toolState';
const WALKTHROUGH_STATE_KEY: string = 'walkthroughState';
const APPLICATIONFORM_STATE_KEY: string = 'applicationFormState';

const initialToolState : ToolState = calculateGraph({
  duration: 10,
  lumpSum: 100000,
  monthlyContribution: 100,
  capitalFund: null,
  digitalFund: null,
  allocation: 50,
  graphFilter: GraphFilters.Blend,
  graphData: [],
  highCaseOutcome: 0,
  lowCaseOutcome: 0,
  mediumCaseOutcome: 0
});

const initialWalkthroughState: WalkthroughState = {
  isComplete: false
};

const initialSignUpState: SignUpState = {
  signUpError: '',
  hasSubmitted: false
};

const applicationFormState: ApplicationFormState = {
  planholder: {
    surname: '',
    name: '',
    dateOfBirth: null,
    gender: '',
    birthPlace: '',
    birthCountry: '',
    countriesOfCitizenship: [],
    email: '',
    idType: '',
    idCountry: '',
    idNumber: '',
    streetAddress: '',
    zipCode: '',
    city: '',
    province: '',
    country: '',
    govOfficial: false,
    govAssocOfficial: false,
    govEmpOfficial: false,
    govExplanation: '',
    employmentStatus: '',
    occupation: '',
    occupationDuties: '',
    employerName: '',
    employerAddress: '',
    employerBusinessType: '',
    incomeLevel: ''
  },
  beneficiary: {
    surname: '',
    name: '',
    dateOfBirth: null,
    country: '',
    countryOfCitizenship: '',
    relationshipToHolder: '',
    percentageBenefit: null,
    hasGuardian: false,
    guardianName: '',
    guardianSurname: '',
    guardianDateOfBirth: null,
    guardianAddress: '',
    guardianCountry: '',
    guardianCountryOfCitizenship: ''
  }
};

const initialRouterState: RouterState = {
  location: {
    pathname: '/',
    search: '',
    hash: ''
  } as Location<string>,
  action: 'POP'
};

const initialAppState : AppState = {
  tool: initialToolState,
  walkthrough: initialWalkthroughState,
  signUp: initialSignUpState,
  applicationForm: applicationFormState,
  router: initialRouterState
};

const saveAppState = (state: AppState) => {
  // Because the entire state must not be persisted - only persist the required sub state
  saveToLocalStorage(TOOL_STATE_KEY, JSON.stringify(state.tool));
  saveToLocalStorage(WALKTHROUGH_STATE_KEY, JSON.stringify(state.walkthrough));
  saveToLocalStorage(APPLICATIONFORM_STATE_KEY, JSON.stringify(state.applicationForm));
};

const loadAppState = () : AppState => {
  const serializedToolState = retrieveFromLocalStorage(TOOL_STATE_KEY);
  const serializedWalkthroughState = retrieveFromLocalStorage(WALKTHROUGH_STATE_KEY);
  const serializedApplicationFormState = retrieveFromLocalStorage(APPLICATIONFORM_STATE_KEY);

  // Load the initial app state then see if the others are valid
  // And if so set the sub state
  const appState : AppState = initialAppState;
  if(serializedToolState) {
    try {
      appState.tool = JSON.parse(serializedToolState)
    } catch {
      // Handle errors later if required
    }
  }
  if(serializedWalkthroughState) {
    try {
      appState.walkthrough = JSON.parse(serializedWalkthroughState);
    } catch {
      // Handle errors later if required
    }
  }
  if(serializedApplicationFormState) {
    try {
      appState.applicationForm = JSON.parse(serializedApplicationFormState);
    } catch {
      // Handle errors later if required
    }
  }

  return appState;
};

const saveToLocalStorage = (key: string, stringifiedData: string) => {
  try {
    localStorage.setItem(key, stringifiedData);
  } catch (err) {
    // Handle errors later if required
  }
};

const retrieveFromLocalStorage = (key: string) => {
  try {
    const serializedData = localStorage.getItem(key);
    return serializedData;
  } catch (err) {
    return null;
  }
};

export {loadAppState, saveAppState}